import {
    Autocomplete,
    Button,
    Chip,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { createStyles, WithStyles, withStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import {
    Close,
    Comment,
    Delete,
    PictureAsPdf,
    Schedule,
    Edit,
} from '@mui/icons-material';
import jsPDF from 'jspdf';
import * as React from 'react';
import {
    Report,
    ReportComment,
    ReportDetail,
    ReportStatus,
    WidgetType,
} from '../../models/report';
import { ccLogoBottom } from '../../shared/assets/images/cc-logo';
import { CCSpinner } from '../../shared/components/cc-spinner';
import {
    DialogResult,
    SimpleDialog,
} from '../../shared/components/simple-dialog';
import { SimpleModal } from '../../shared/components/simple-modal';
import { EnumerationHelper, isValidEmail } from '../../shared/utils';
import { DateFormatting } from '../../utils/date-formatting';
import { FacesReviewHelper } from '../../utils/faces-review-helper';
import { CCAgrees } from '../cc-agrees/cc-agrees';
import { CommentEditor } from '../comment-editor';
import { FaceType } from '../faces-review';
import { FacesReviewBtn } from '../faces-review-button';
import {
    WidgetContentGenerator,
    WidgetContentGeneratorType,
    WidgetDataForPrinting,
    WidgetPhotoData,
} from '../widget-content-generator';
import EditIcon from '@mui/icons-material/Edit';
import { TabControl } from '../../shared/components/tab-control';
import { WorkLogEditor } from '../work-log-editor';
import { TimeTrackComment, TimeTrackPayload } from '../../models/report/report-timetrack';
import InventoryIcon from '@mui/icons-material/Inventory';

const PDF_IMAGE_MAX_WIDTH = 60;
const DEBOUNCE_TIME = 300;
const STATUSES_MENU_ITEMS = Object.keys(ReportStatus).map((key, index) => {
    return (
        <MenuItem key={`k-${index}`} value={key}>
            {ReportStatus[key as keyof typeof ReportStatus]}
        </MenuItem>
    );
});

const styles = (theme: Theme) =>
    createStyles({
        root: {
            minWidth: '97%',
            minHeight: '97%',
            height: '97%',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                minWidth: 'auto',
                overflow: 'auto',
            },
        },
        headerContainer: {
            display: 'flex',
            flexDirection: 'column',
        },
        headerContentWrapper: {
            display: 'flex',
            flex: 1,
        },
        headerContent: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            padding: '1em',
        },
        closeButton: {
            alignSelf: 'flex-start',
            padding: '0.2em',
            margin: '0.5em',
        },
        timeStampContainer: {
            display: 'flex',
            '& $timeStamp, & $folderName': {
                fontSize: '1em',
            },
        },
        timeStamp: {
            marginRight: '2em',
        },
        folderName: {},
        selectsContainer: {
            display: 'flex',
            justifyContent: 'flex-start',
            margin: '0 0.4em 0 1em',
            height: '6em',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                height: 'auto',
            },
        },
        contentContainer: {
            display: 'flex',
            padding: '0 0.4em',
        },
        status: {
            marginTop: '1em',
            marginRight: '5em',
            minWidth: '12em',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginRight: '1em',
                width: '100%',
            },
        },
        assignedTo: {
            width: '19em',
            marginTop: '0',
            '&.cc-is-valid': {
                marginRight: '1em'
            },
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginTop: '1em',
                width: '100%',
            },
        },
        verticalTextContainer: {
            display: 'flex',
            flexDirection: 'column',
            margin: '0 7em 0 0',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginRight: '0',
            },
        },
        editAssignmentContainer: {
            display: 'flex',
            flexDirection: 'column',
            margin: '0 1em 0 0',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginRight: '1em',
                marginTop: '1em',
                width: '100%',
            },
        },
        applyButton: {
            margin: '0 1em 0 2em',
        },
        buttonText: {
            marginLeft: '1em',
        },
        agrees: {},
        assignedToIcon: {
            marginRight: '0.3em',
        },
        tooltip: {
            fontSize: '1em',
        },
        bottomContentContainer: {
            flexDirection: 'row-reverse',
            flex: 1,
            paddingTop: '2em',
            [theme.breakpoints.only('xs')]: {
                flexDirection: 'column',
                overflow: 'auto',
                paddingTop: 0,
            },
            '&$isEndUser': {
                height: '73%',
                [theme.breakpoints.between(
                    'xs',
                    theme.breakpoints.values.iPhone8_Landscape
                )]: {
                    height: 'auto',
                    paddingTop: 0,
                },
            },
        },
        isEndUser: {},
        bottomContent: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
        },
        bottomContentTitle: {
            display: 'flex',
            minHeight: '2em',
        },
        bottomContentTitleIcon: {
            marginRight: '0.3em',
        },
        bottomContentSubtitle: {
            fontWeight: 'bold',
            textTransform: 'uppercase',
            color: theme.palette.grey[500],
            marginTop: '1em',
        },
        bottomContentTextField: {
            marginTop: '0.5em',
        },
        bottomContentGroup: {
            position: 'relative',
            padding: '0 3em 0 2.3em',
            '& hr': {
                margin: '2em 0',
            },
        },
        bottomContentRight: {
            display: 'flex',
            marginLeft: 'auto',
            flexDirection: 'column',
            maxWidth: '16em',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginLeft: '0',
                maxWidth: 'none',
                paddingBottom: '2.5em',
            },
        },
        exportToPdfButton: {},
        exportToPdfButtonTop: {
            alignSelf: 'flex-start',
            [theme.breakpoints.between('xs', theme.breakpoints.values.sm)]: {
                display: 'none',
            },
        },
        exportToPdfButtonBottom: {
            alignSelf: 'flex-start',
            marginTop: '2em',
            padding: '0.35em 1.2em',
            [theme.breakpoints.up(theme.breakpoints.values.sm)]: {
                display: 'none',
            },
        },
        editCategoryButton: {
            display: 'flex',
            transform: 'scale(0.8)',
            height: '3em',
            alignItems: 'center',
            '& .MuiSvgIcon-root': {
                marginRight: '0.5em',
            },
        },
        reportedByEmail: {
            overflowWrap: 'break-word',
            wordBreak: 'break-word',
        },
        widgetPhoto: {
            width: 185,
            marginTop: '1em',
        },
        floorPlanImage: {
            maxWidth: 210,
        },
        bodyContent: {
            flex: '1',
            padding: '0.6em',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'auto',
        },
        commentEditor: {
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                marginBottom: '5em',
            },
        },
        reviewBtn: {
            width: '11em',
            margin: '1em',
            height: '3em',
        },
        editReportBtnSection: {
            margin: '0 1em 0 auto',
            display: 'flex',
            alignItems: 'center',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                margin: '1em 1em 0 0',
            },
        },
        assignedBtnSection: {
            marginTop: '1.8em',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                marginBottom: '1em',
                position: 'absolute',
                right: '0',
                marginTop: '5.5em',
            },
        },
        assignmentAutocomplete: {
            marginTop: '1em',
        },
        assignmentLabel: {
            marginTop: '0.5em',
            letterSpacing: '0.00938em',
        },
        assignmentText: {
            width: '19em',
            display: 'flex',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            [theme.breakpoints.between(
                'xs',
                theme.breakpoints.values.iPhone8_Landscape
            )]: {
                width: '100%',
            },
            '& span': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                width: '100%',
                whiteSpace: 'nowrap',
            }
        },
        editAssignmentIcon: {
            padding: '0 0.3em',
            position: 'relative',
            bottom: '0.2em',
        },
        tabContainer: {
            padding: '1em 3em 0 2.3em'
        },
        tabHeader: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            marginBottom: '1em'
        },
        archivedChip: {
            fontWeight: 600,
            padding: '0 0.5em',
            marginTop: '0.5em',
            width: '8em'
        }
    });

interface EmailFilter { 
    user_id: string;
    email: string;
}

interface Props extends WithStyles<typeof styles> {
    className?: string;
    open: boolean;
    isPortal: boolean;
    vocFeedbackReports: Report[];
    isStatusRefreshing?: boolean;
    areCommentsLoading?: boolean;
    showUpdateCategory?: boolean;
    isUsersFilterLoading?: boolean;
    reportDetail?: ReportDetail;
    comments?: ReportComment[];
    reportTimeTracked?: string;
    timeTrackComments?: TimeTrackComment[];
    onClose?: () => void;
    onStatusChange?: (reportId: string, status: ReportStatus) => void;
    onAssigneeChange?: (reportId: string, email: string) => void;
    onCommentPost?: (reportId: string, commentText: string) => Promise<void>;
    onDeleteReport?: (reportId: string) => void;
    onReturnToVoc?: () => void;
    getCategoryList?: (markerId: string) => void;
    getUserbyEmail?: (email: string) =>  Promise<string[]>;
    onTimeTrackPost?: (timeTrack: TimeTrackPayload) => Promise<void>;
}
interface States {
    assignedTo: string;
    assignedToChanged: boolean;
    isEmailValid: boolean;
    isDeleteActionModalOpen: boolean;
    isFilterFolderOpen: boolean;
    assignmentMatches: string[];
    emailList: string[];
    isEditAssignment: boolean;
}

const DEFAULT_STATE = {
    assignedTo: '',
    assignedToChanged: false,
    isEmailValid: true,
    isDeleteActionModalOpen: false,
    isFilterFolderOpen: false,
    assignmentMatches: [],
    emailList: [],
    isEditAssignment: false
};

export type WidgetContentGeneratorInstanceType =
    typeof WidgetContentGenerator & {
        generateOutputForPrint: () => WidgetDataForPrinting[];
    };

class WorkOrderDetails extends React.Component<Props, States> {
    public static defaultProps = {
        isPortal: true,
        showUpdateCategory: false,
        isUsersFilterLoading: false
    };

    state = {
        ...DEFAULT_STATE
    };

    widgetContentGeneratorInstance: WidgetContentGeneratorInstanceType | null = null;
    setWidgetContentGeneratorInstance = (element: WidgetContentGeneratorInstanceType) => {
        this.widgetContentGeneratorInstance = element;
    };

    debounceTimeout?: NodeJS.Timeout = undefined;

    onCloseClicked = () => {
        const { onClose } = this.props;

        this.setState(DEFAULT_STATE);

        if (onClose) {
            onClose();
        }
    };

    onStatusChanged = (event: SelectChangeEvent<string>) => {
        const { reportDetail, onStatusChange } = this.props;

        if (!onStatusChange || !reportDetail) {
            return;
        }
        const statusKey = event.target.value;
        onStatusChange(
            reportDetail.fullId,
            ReportStatus[statusKey as keyof typeof ReportStatus]
        );
    };

    onAssignedToChanged = (event: React.SyntheticEvent<Element, Event>, value: string, reason: string) => {
        // Avoid unnecessary state updates by default reset autocomplete
        if (value === '' && reason === 'reset') {
            return;
        }

        this.setState({
            assignedTo: value.trim(),
        }, () => {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout as NodeJS.Timeout);
            }

            const { reportDetail, getUserbyEmail } = this.props;

            if (reportDetail && getUserbyEmail) {
                this.debounceTimeout = setTimeout(() => {
                    const assignedToProperty = reportDetail
                        ? reportDetail.assignedTo
                        : undefined;
                    const isEmailValid = isValidEmail(value);

                    // Get the filtered user list
                    getUserbyEmail(value).then((response: string[]) => {
                        const assignmentsList: string[] = [];
                        for (const item of response as unknown as EmailFilter[]) {
                            if (!(assignmentsList.includes(item.email))) {
                                assignmentsList.push(item.email);
                            }
                        }

                        this.setState({
                            assignmentMatches: assignmentsList
                        });
                    });

                    this.setState({
                        isFilterFolderOpen: reason === 'input', // open only if the user is typing
                        assignedToChanged: !!value && isEmailValid && value !== assignedToProperty,
                        isEmailValid
                    });
                    
                }, DEBOUNCE_TIME);
            } else {
                this.setState({
                    isFilterFolderOpen: false,
                    assignmentMatches: [],
                    assignedToChanged: false,
                    isEmailValid: true
                });
            }
        });
    };

    getFilterOptionSelected = (option: EmailFilter, value: EmailFilter) => {
        return option.email === value.email;
    };

    onAssignedToOkClicked = () => {
        const { onAssigneeChange, reportDetail } = this.props;
        const { assignedTo } = this.state;

        if (!onAssigneeChange || !reportDetail || !assignedTo) {
            return;
        }

        onAssigneeChange(reportDetail.fullId, assignedTo);
        this.setState(DEFAULT_STATE);
    };

    onAssignedToCancelClicked = () => {
        this.setState(DEFAULT_STATE);
    };

    onFormSubmitted = (
        event: React.FormEvent<HTMLFormElement> & React.SyntheticEvent<unknown>
    ) => {
        event.preventDefault();
        this.onAssignedToOkClicked();
    };

    onDeleteClicked = () => {
        this.setState({ isDeleteActionModalOpen: true });
    };

    onCommentPosted = (commentText: string): Promise<void> => {
        const { reportDetail, onCommentPost } = this.props;

        if (!onCommentPost || !reportDetail) {
            return Promise.resolve();
        }

        return onCommentPost(reportDetail.fullId, commentText);
    };

    onDeleteDialogAction = (result: DialogResult) => {
        if (result === DialogResult.Ok) {
            const { reportDetail, onDeleteReport } = this.props;

            if (onDeleteReport) {
                onDeleteReport(reportDetail ? reportDetail.id : '');
            }
        }

        this.setState(DEFAULT_STATE);
    };

    onReturnToVocClicked = () => {
        const { onReturnToVoc } = this.props;
        if (onReturnToVoc) {
            onReturnToVoc();
        }
    };

    generatePDFButton = (extraClasses: string) => {
        const { classes, isPortal } = this.props;

        if (!isPortal) {
            return '';
        }
        const buttonClasses = `${classes.exportToPdfButton}${
            extraClasses ? ` ${extraClasses}` : ''
        }`;
        return (
            <Button
                className={buttonClasses}
                variant="outlined"
                onClick={this.onDownloadPDFClicked}
                color="info"
            >
                <PictureAsPdf />
                <Typography className={classes.buttonText} variant="overline">
                    DOWNLOAD PDF
                </Typography>
            </Button>
        );
    };

    onDownloadPDFClicked = () => {
        this.generatePDFPage();
    };

    onUpdateCategoryClicked = () => {
        const { reportDetail, getCategoryList } = this.props;

        if (reportDetail && getCategoryList) {
            getCategoryList(reportDetail.markerId);
        }
    };

    generatePDFPage = () => {
        const { reportDetail, comments } = this.props;
        if (!reportDetail) {
            return;
        }

        // The margin top/bottom for the PDF page
        const verticalMargin = 20;

        // Coordinates values are the absolute measures that jsPDF uses for PDF file creation
        // this values starting as X and Y axis positions (X=0 and Y=0 means the top left corner of page)
        // Check https://github.com/MrRio/jsPDF for more info
        let verticalCoordinate = verticalMargin;

        // The margin right for the PDF file
        const marginHorizontal = 20;
        const tabSpace = 10;

        // This the maximum value for Y coordinate that a page has
        // This number is dependent of the layout height for the PDF file
        const maxVerticalCoordinate = 288 - verticalMargin;

        function getNextVerticalCoordinate(
            rowsAdded = 1,
            nextLineIncrement = 6,
            additionalLineIncrement = 0
        ): number {
            const currentValue = verticalCoordinate;
            verticalCoordinate +=
                rowsAdded * nextLineIncrement + additionalLineIncrement;
            return currentValue;
        }

        const {
            assignedTo,
            email,
            reportSequenceNumber,
            folderName,
            markerName,
            moduleName,
            id: reportId,
            status,
            timeStamp,
            username,
        } = reportDetail;

        const timeStampAndFolder = `${DateFormatting.formatMarkerTimestamp(
            timeStamp
        )}     ${folderName}`;
        const pdfDocument = new jsPDF({ format: 'letter' });
        pdfDocument.addImage(ccLogoBottom, 'PNG', 170, 5, 30, 18);
        pdfDocument
            .setFont('Helvetica', 'bold')
            .setFontSize(18)
            .text(markerName, marginHorizontal, getNextVerticalCoordinate(2))
            .setFont('Helvetica', 'normal')
            .setFontSize(12)
            .text(
                timeStampAndFolder,
                marginHorizontal,
                getNextVerticalCoordinate()
            )
            .setFont('Helvetica', 'bold')
            .setFontSize(12)
            .text(
                `ID : ${reportSequenceNumber ? reportSequenceNumber : 'N/A'}`,
                marginHorizontal,
                getNextVerticalCoordinate()
            )
            .setFont('Helvetica', 'normal')
            .setFontSize(14)
            .text(
                [
                    `Reported by : ${username}`,
                    email,
                    `Type : ${moduleName}`,
                    `Status : ${status}`,
                    `Assigned to : ${assignedTo ? assignedTo : '[Unassigned]'}`,
                ],
                marginHorizontal,
                getNextVerticalCoordinate(7),
                { lineHeightFactor: 1.3 }
            )
            .setFontSize(18)
            .text('Summary', marginHorizontal, getNextVerticalCoordinate(2));

        //#region Summary
        const { widgetContentGeneratorInstance } = this;
        const lineIncrement = 6;
        if (widgetContentGeneratorInstance !== null) {
            const widgetDataElements =
                widgetContentGeneratorInstance.generateOutputForPrint();
            widgetDataElements.forEach((widgetData: WidgetDataForPrinting) => {
                const { widgetType, data } = widgetData;
                switch (widgetType) {
                    case WidgetType.Label:
                    case WidgetType.TextField:
                        pdfDocument
                            .setFontSize(12)
                            .text(
                                data as string,
                                marginHorizontal + tabSpace,
                                getNextVerticalCoordinate()
                            );
                        break;
                    case WidgetType.Photo: {
                        const {
                            format,
                            height,
                            imageEncoded,
                            timeStamp: imageTimeStamp,
                            width,
                        } = data as WidgetPhotoData;
                        if (imageEncoded && format) {
                            const shrunkWidth = Math.min(
                                width,
                                PDF_IMAGE_MAX_WIDTH
                            );
                            const shrunkHeight = Math.ceil(
                                (shrunkWidth * height) / width
                            );
                            const multiplier = Math.floor(
                                shrunkHeight / lineIncrement
                            );
                            pdfDocument.addImage(
                                imageEncoded,
                                format,
                                marginHorizontal + tabSpace,
                                getNextVerticalCoordinate(multiplier),
                                shrunkWidth,
                                shrunkHeight
                            );
                        }
                        if (imageTimeStamp) {
                            pdfDocument
                                .setFontSize(10)
                                .text(
                                    imageTimeStamp,
                                    marginHorizontal + tabSpace,
                                    getNextVerticalCoordinate()
                                );
                        }
                        break;
                    }
                }
            });
            getNextVerticalCoordinate(1);
        }
        //#endregion

        if (comments && comments.length > 0) {
            pdfDocument
                .setFontSize(18)
                .text(
                    'Activity',
                    marginHorizontal,
                    getNextVerticalCoordinate(2)
                );
            comments.forEach((commentItem: ReportComment) => {
                const {
                    comment,
                    info: { username: commentUsername },
                    timeStamp: commentTimeStamp,
                } = commentItem;

                // Split a string at every n characters (110 in this case) or to nearest previous space
                // This number is dependent of the layout width for the PDF file
                const splitComment = comment
                    .replace(/[\s\S]{1,110}(?!\S)/g, '$&\n')
                    .split('\n');
                const updatedComment = splitComment.map((line) => line.trim());

                // Check if coordinate exceed the maximum for create another page
                if (verticalCoordinate > maxVerticalCoordinate) {
                    verticalCoordinate = verticalMargin;
                    pdfDocument.addPage();
                }

                pdfDocument
                    .setFontSize(10)
                    .text(
                        `${commentUsername}    ${DateFormatting.formatMarkerTimestamp(
                            commentTimeStamp
                        )}`,
                        marginHorizontal + tabSpace,
                        getNextVerticalCoordinate()
                    )
                    .text(
                        updatedComment,
                        marginHorizontal + tabSpace,
                        getNextVerticalCoordinate(updatedComment.length, 4, 2)
                    );
            });
        }

        pdfDocument.save(
            `cc-${reportSequenceNumber ? reportSequenceNumber : reportId}.pdf`
        );
    };

    onEditAssignedClicked = () => {
        this.setState({
            isEditAssignment: true
        }, () => {
            // Trigger an empty change event to display the initial assignment list
            this.onAssignedToChanged({} as React.SyntheticEvent<Element, Event>, '', 'input');
        });
    };

    updateEmailList = (email: string) => {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout as NodeJS.Timeout);
        }

        const { reportDetail, getUserbyEmail } = this.props;

        if (reportDetail && getUserbyEmail) {
            this.debounceTimeout = setTimeout(() => {
                // Get the filtered user list
                getUserbyEmail(email).then((response: string[]) => {
                    const emailList: string[] = [];
                    for (const item of response as unknown as EmailFilter[]) {
                        if (!(emailList.includes(item.email))) {
                            emailList.push(item.email);
                        }
                    }

                    this.setState({ emailList });
                });
            }, DEBOUNCE_TIME);
        } else {
            this.setState({emailList: []});
        }
    };

    public render() {
        const {
            classes,
            className,
            comments,
            open,
            isPortal,
            isStatusRefreshing,
            areCommentsLoading,
            reportDetail,
            vocFeedbackReports,
            showUpdateCategory,
            isUsersFilterLoading,
            timeTrackComments,
            reportTimeTracked,
            onTimeTrackPost
        } = this.props;

        if (!reportDetail) {
            return '';
        }

        const {
            assignedTo: assignedToState,
            assignedToChanged,
            isEmailValid,
            isDeleteActionModalOpen,
            isFilterFolderOpen,
            assignmentMatches,
            isEditAssignment,
            emailList
        } = this.state;

        const {
            agrees,
            assignedTo,
            email,
            folderName,
            markerName,
            miniMapImageEncoded,
            moduleName,
            timeStamp,
            reportSequenceNumber: id,
            status,
            username,
            userCanLeaveFeedback,
            userFeedback,
            widgets,
            archived
        } = reportDetail;
        const rootClasses = `${classes.root}${
            className ? ` ${className}` : ''
        }`;
        const currentStatus: string | undefined =
            EnumerationHelper.getEnumKeyFromValue(
                ReportStatus,
                status ? status : ''
            );
        const currentAssignedTo = assignedToState
            ? assignedToState
            : assignedTo;
        const isEmailInvalid = !!currentAssignedTo && !isEmailValid;
        const assignedToClasses = `${classes.assignedTo}${
            isEmailInvalid ? '' : ' cc-is-valid'
        }`;
        const timeStampFormatted =
            DateFormatting.formatMarkerTimestamp(timeStamp);
        const isVocView =
            status === 'Closed' && !!userFeedback && userFeedback.length > 0;
        const isVocReadonly = userCanLeaveFeedback === false && isVocView;

        let faceType = FaceType.None;
        if (userFeedback && userCanLeaveFeedback === false) {
            faceType = FacesReviewHelper.getCurrentFace(
                userFeedback[userFeedback.length - 1].content
            );
        }

        const tabLabels = !onTimeTrackPost || !timeTrackComments
            ? [{label: 'Comments'}]
            : [{label: 'Comments'}, {label: 'Work Log'}];
        const isArchived = archived ? archived : false;
        const isEditableMode = isPortal && !isArchived;

        return (
            <SimpleModal
                classes={{
                    modalContainer: rootClasses,
                }}
                open={open}
                contentClasses={classes.bodyContent}
                header={
                    <div className={classes.headerContainer}>
                        <div className={classes.headerContentWrapper}>
                            <div className={classes.headerContent}>
                                <Typography variant="h5">
                                    {markerName}
                                </Typography>
                                <div className={classes.timeStampContainer}>
                                    <Typography
                                        className={classes.timeStamp}
                                        variant="caption"
                                    >
                                        {timeStampFormatted}
                                    </Typography>
                                    <Typography
                                        className={classes.folderName}
                                        variant="caption"
                                    >
                                        {folderName}
                                    </Typography>
                                </div>
                                <Typography variant="subtitle2">
                                    ID: {id ? id : 'N/A'}
                                </Typography>
                                { isArchived && 
                                    <Chip
                                        className={classes.archivedChip}
                                        color="warning"
                                        size="small"
                                        label="Archived"
                                        icon={<InventoryIcon />}
                                    />
                                }
                            </div>
                            {
                                (!isEditableMode && isVocView) &&
                                    (
                                        <FacesReviewBtn
                                            className={classes.reviewBtn}
                                            onClick={this.onReturnToVocClicked}
                                            buttonText="Rate"
                                            selectedOption={faceType}
                                            isReadOnly={userCanLeaveFeedback}
                                        />
                                    )
                            }
                            {
                                (isEditableMode ||
                                !isVocView ||
                                isVocReadonly ||
                                vocFeedbackReports.length > 1) &&
                                    (
                                        <IconButton
                                            className={classes.closeButton}
                                            onClick={this.onCloseClicked}
                                        >
                                            <Close />
                                        </IconButton>
                                    )
                            }
                        </div>
                        {!isStatusRefreshing && (
                            <>
                                <Divider />
                                <form
                                    className={classes.selectsContainer}
                                    autoComplete="off"
                                    onSubmit={this.onFormSubmitted}
                                >
                                    {isEditableMode ? (
                                        <FormControl
                                            variant="standard"
                                            className={classes.status}
                                        >
                                            <InputLabel htmlFor="cc-status">
                                                STATUS
                                            </InputLabel>
                                            <Select
                                                value={currentStatus}
                                                onChange={this.onStatusChanged}
                                                inputProps={{
                                                    name: 'status',
                                                    id: 'cc-status',
                                                }}
                                            >
                                                {STATUSES_MENU_ITEMS}
                                            </Select>
                                        </FormControl>
                                    ) : (
                                        <div
                                            className={
                                                classes.verticalTextContainer
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.bottomContentSubtitle
                                                }
                                                variant="overline"
                                            >
                                                Status
                                            </Typography>
                                            <Typography variant="body2">
                                                {currentStatus}
                                            </Typography>
                                        </div>
                                    )}
                                    {isEditableMode ? (
                                        <div
                                            className={
                                                classes.editAssignmentContainer
                                            }
                                        >
                                            {
                                                isEditAssignment ?
                                                    <Autocomplete
                                                        className={classes.assignmentAutocomplete}
                                                        open={isFilterFolderOpen}
                                                        inputValue={assignedToState}
                                                        onInputChange={this.onAssignedToChanged}
                                                        options={assignmentMatches}
                                                        getOptionLabel={(option) => option}
                                                        loading={isUsersFilterLoading}
                                                        clearOnBlur={true}
                                                        blurOnSelect={true}
                                                        renderOption={(props, option) => {
                                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                            const { key, ...optionProps } = props as any;
                                                            return (
                                                                <li key={key} {...optionProps}>
                                                                    {option}
                                                                </li>
                                                            );
                                                        }}
                                                        renderInput={(params) => 
                                                            <TextField
                                                                {...params}
                                                                className={assignedToClasses}
                                                                id="assigned_to"
                                                                name="assigned_to"
                                                                margin="normal"
                                                                label="ASSIGNED TO"
                                                                variant="standard"
                                                                placeholder="Enter the assignment"
                                                                error={isEmailInvalid}
                                                                helperText={
                                                                    isEmailInvalid
                                                                        ? 'Please enter a valid email'
                                                                        : ''
                                                                }
                                                                InputLabelProps={{
                                                                    shrink: true,
                                                                }}
                                                            />
                                                        }
                                                    />
                                                    : <>
                                                        <Typography
                                                            className={classes.assignmentLabel}
                                                            variant="overline"
                                                        >
                                                            Assigned To
                                                        </Typography>
                                                        <Typography
                                                            className={classes.assignmentText}
                                                            variant="body2"
                                                        >
                                                            <span>
                                                                {currentAssignedTo &&
                                                                currentAssignedTo !== ''
                                                                    ? currentAssignedTo
                                                                    : 'Unassigned'}
                                                            </span>
                                                            <IconButton
                                                                className={classes.editAssignmentIcon}
                                                                aria-label="edit"
                                                                onClick={this.onEditAssignedClicked}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Typography>
                                                    </>
                                            }
                                        </div>
                                    ) : (
                                        <div
                                            className={
                                                classes.status
                                            }
                                        >
                                            <Typography
                                                className={
                                                    classes.bottomContentSubtitle
                                                }
                                                variant="overline"
                                            >
                                                Assigned To
                                            </Typography>
                                            <Typography variant="body2">
                                                {currentAssignedTo &&
                                                currentAssignedTo !== ''
                                                    ? currentAssignedTo
                                                    : 'Unassigned'}
                                            </Typography>
                                        </div>
                                    )}
                                    {
                                        assignedToChanged && (
                                            <div className={classes.assignedBtnSection}>
                                                <Button
                                                    className={classes.applyButton}
                                                    variant="contained"
                                                    color="secondary"
                                                    size="small"
                                                    onClick={this.onAssignedToOkClicked}
                                                >
                                                    APPLY
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={
                                                        this.onAssignedToCancelClicked
                                                    }
                                                >
                                                    CANCEL
                                                </Button>
                                            </div>
                                        )
                                    }
                                    <div className={classes.editReportBtnSection}>
                                        <CCAgrees
                                            className={`${classes.agrees} ${
                                                !isEditableMode ? classes.isEndUser : ''
                                            }`}
                                            count={
                                                agrees && agrees.length > 0
                                                    ? agrees.length
                                                    : 0
                                            }
                                            tooltip="Agrees"
                                        />
                                        {
                                            (isEditableMode && showUpdateCategory) && (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="info"
                                                    onClick={
                                                        this.onUpdateCategoryClicked
                                                    }
                                                    className={
                                                        classes.editCategoryButton
                                                    }
                                                >
                                                    <Edit /> Edit Category
                                                </Button>
                                            )
                                        }
                                        {
                                            isEditableMode && (
                                                <Tooltip
                                                    title="Delete Report"
                                                    classes={{
                                                        tooltip: classes.tooltip,
                                                    }}
                                                >
                                                    <IconButton
                                                        onClick={this.onDeleteClicked}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            )
                                        }
                                    </div>
                                </form>
                            </>
                        )}
                        <Divider />
                    </div>
                }
                footer={' '}
            >
                <CCSpinner
                    loading={!!isStatusRefreshing}
                    overlayVisible={true}
                    size={100}
                >
                    <div
                        className={`${classes.bottomContentContainer} ${
                            classes.contentContainer
                        } ${!isEditableMode ? classes.isEndUser : ''}`}
                    >
                        <div className={classes.bottomContentRight}>
                            {this.generatePDFButton(
                                classes.exportToPdfButtonTop
                            )}
                            <Typography
                                className={classes.bottomContentSubtitle}
                                variant="overline"
                            >
                                Category
                            </Typography>
                            <Typography variant="body2">
                                {moduleName}
                            </Typography>
                            <Typography
                                className={classes.bottomContentSubtitle}
                                variant="overline"
                            >
                                REPORTED BY
                            </Typography>
                            <Typography variant="body2">{username}</Typography>
                            <Typography
                                className={classes.reportedByEmail}
                                variant="body2"
                            >
                                {email}
                            </Typography>
                            <Typography variant="caption">
                                {timeStampFormatted}
                            </Typography>
                            {
                                reportTimeTracked && (
                                    <>
                                        <Typography
                                            className={classes.bottomContentSubtitle}
                                            variant="overline"
                                        >
                                            TOTAL TIME WORKED
                                        </Typography>
                                        <Typography variant="body2">{reportTimeTracked}</Typography>
                                    </>
                                )
                            }
                            {
                                miniMapImageEncoded && (
                                    <>
                                        <Typography
                                            className={
                                                classes.bottomContentSubtitle
                                            }
                                            variant="overline"
                                        >
                                            FLOOR PLAN
                                        </Typography>
                                        <img
                                            alt="map"
                                            className={classes.floorPlanImage}
                                            src={miniMapImageEncoded}
                                        />
                                    </>
                                )
                            }
                            {this.generatePDFButton(
                                classes.exportToPdfButtonBottom
                            )}
                        </div>
                        <div className={classes.bottomContent}>
                            <div className={classes.bottomContentTitle}>
                                <Comment
                                    className={classes.bottomContentTitleIcon}
                                />
                                <Typography variant="h5">Summary</Typography>
                            </div>
                            <div className={classes.bottomContentGroup}>
                                <WidgetContentGenerator
                                    innerRef={this.setWidgetContentGeneratorInstance}
                                    classes={{
                                        widgetLabel:
                                            classes.bottomContentSubtitle,
                                        widgetTextFieldRead:
                                            classes.bottomContentTextField,
                                        widgetPhotoRead: classes.widgetPhoto,
                                    }}
                                    widgets={widgets}
                                    generatorType={
                                        WidgetContentGeneratorType.Read
                                    }
                                    timeStamp={timeStampFormatted}
                                />
                                <Divider />
                            </div>
                            <div className={classes.bottomContentTitle}>
                                <Schedule
                                    className={classes.bottomContentTitleIcon}
                                />
                                <Typography variant="h5">Activity</Typography>
                            </div>
                            <TabControl
                                classes={{
                                    root: classes.tabContainer,
                                    paper: classes.tabHeader
                                }}
                                tabsDescriptors={tabLabels}
                            >
                                <div
                                    className={`${classes.bottomContentGroup} ${classes.commentEditor}`}
                                >
                                    <CommentEditor
                                        comments={comments}
                                        isLoading={areCommentsLoading}
                                        onCommentPost={this.onCommentPosted}
                                        isDisabled={isArchived}
                                    />
                                </div>
                                {
                                    (!isArchived && onTimeTrackPost && timeTrackComments) && (
                                        <div
                                            className={`${classes.bottomContentGroup} ${classes.commentEditor}`}
                                        >
                                            <WorkLogEditor
                                                comments={timeTrackComments}
                                                isLoading={areCommentsLoading}
                                                onTimeTrackPost={onTimeTrackPost}
                                                updateEmailList={this.updateEmailList}
                                                emailList={emailList}
                                            />
                                        </div>
                                    )
                                }
                            </TabControl>
                        </div>
                    </div>
                </CCSpinner>
                <SimpleDialog
                    open={isDeleteActionModalOpen}
                    titleText=""
                    contentText="Are you sure you want to delete the selected report?"
                    buttonOkLabel="YES"
                    buttonCancelLabel="NO"
                    onDialogResult={this.onDeleteDialogAction}
                />
            </SimpleModal>
        );
    }
}

const MUIComponent = withStyles(styles)(WorkOrderDetails);
export { MUIComponent as WorkOrderDetails };
